import { Spacing } from '@persona-id/design-tokens';
import { ReactNode } from 'react';
import Markdown from 'shared/components/Markdown';
import styled from 'styled-components';

import Spacer from 'design-system/components/Spacer';
import Button from 'inquiry/components/Button';
import Dialog from 'inquiry/components/Dialog';
import ThemedParagraph from 'inquiry/components/ThemedParagraph';
import ThemedTitle from 'inquiry/components/ThemedTitle';
import { colorCss } from 'lib/inquiryStyles';
import { SerializedStepGovernmentIdStyles } from 'lib/interfaces/StepsConfig';
import ThemedSvg from 'verify/components/ThemedSvg';

const HelpModalPrompt = styled(Markdown)`
  font-size: 14px;
  line-height: 21px;
`;

const HelpModalWarning = styled(Markdown)`
  background-color: #fff7de;
  padding: ${Spacing[3]} ${Spacing[4]};
  font-size: 14px;
  line-height: 21px;
`;

const HelpModalImageContainer = styled.div`
  display: flex;
  justify-content: space-around;
`;

const HintListMarkdown = styled(Markdown)`
  font-size: 14px;
  line-height: 21px;
  ul {
    border-spacing 0 8px;

    display: table;

    li {
      display: table-row;
    }

    li::before {
      display: table-cell;
      padding-right: 12px;
    }
  }
`;

const StickyButton = styled(Button)`
  position: sticky;
  bottom: 0;
`;

interface GovernmentIdHelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  prompt: string;
  warning?: string;
  pictograph: ReactNode;
  hints: string;
  continueBtnLabel: string;
  styles?: Pick<SerializedStepGovernmentIdStyles, 'base' | 'buttons' | 'captureHintIcon'>;
}

const GovernmentIdHelpModal = ({
  isOpen,
  onClose,
  title,
  prompt,
  warning,
  pictograph,
  hints,
  continueBtnLabel,
  styles,
}: GovernmentIdHelpModalProps) => {
  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      styles={styles?.base?.modal}
      data-test='help-dialog'
      aria-labelledby='help-dialog__title'
      aria-describedby='help-dialog__description'
    >
      <ThemedTitle id='help-dialog__title' styles={styles?.base?.titleStyle}>
        <Markdown>{title}</Markdown>
      </ThemedTitle>
      {prompt && (
        <>
          <ThemedParagraph id='help-dialog__description' styles={styles?.base?.textStyle}>
            <HelpModalPrompt>{prompt}</HelpModalPrompt>
          </ThemedParagraph>
          <Spacer height={2.5} />
        </>
      )}
      {warning && (
        // This is intentionally NOT styled because the warning background color is currently hardcoded.
        // Can support with SST in the future.
        <>
          <ThemedParagraph id='help-dialog__warning'>
            <HelpModalWarning>{warning}</HelpModalWarning>
          </ThemedParagraph>
          <Spacer height={2.5} />
        </>
      )}
      <HelpModalImageContainer>
        <ThemedSvg
          $primaryColor={colorCss(styles?.captureHintIcon?.fill?.color)}
          $strokeColor={colorCss(styles?.captureHintIcon?.stroke?.color)}
        >
          {pictograph}
        </ThemedSvg>
      </HelpModalImageContainer>
      <Spacer height={2} />
      <ThemedParagraph id='help-dialog__description' styles={styles?.base?.textStyle}>
        <HintListMarkdown>{hints}</HintListMarkdown>
      </ThemedParagraph>
      <StickyButton
        type='button'
        id='help-dialog__btn'
        onClick={onClose}
        styles={styles?.buttons?.primary}
      >
        {continueBtnLabel}
      </StickyButton>
    </Dialog>
  );
};

export default GovernmentIdHelpModal;
